"use client";

import { type ColumnDef, type ColumnSizingState, type VisibilityState } from "@tanstack/react-table";
import { useSyncExternalStore } from "react";
import { z } from "zod";

import { getSessionItem, setSessionItem } from "@/src/lib/session-storage";

const columnSizingSchema = z.record(z.string(), z.number());

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function parseColumnSizing(raw: string | null): ColumnSizingState {
  if (!raw) return {};

  try {
    const parsed = columnSizingSchema.safeParse(JSON.parse(raw));

    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

const DEFAULT_COLUMN_SIZE = 150;

function getColumnId<TData, TValue>(column: ColumnDef<TData, TValue>): string | undefined {
  if (column.id) return column.id;
  if ("accessorKey" in column) return String(column.accessorKey).replaceAll(".", "_");

  return typeof column.header === "string" ? column.header : undefined;
}

export function getFillColumnSizing<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  columnVisibility: VisibilityState,
  containerWidth: number
): ColumnSizingState {
  const visibleColumns = columns.flatMap((column) => {
    const id = getColumnId(column);

    if (!id || columnVisibility[id] === false) return [];

    return [{ id, size: column.size ?? DEFAULT_COLUMN_SIZE, isResizable: column.enableResizing !== false }];
  });
  const resizableColumns = visibleColumns.filter((column) => column.isResizable);
  const fixedWidth = visibleColumns
    .filter((column) => !column.isResizable)
    .reduce((total, column) => total + column.size, 0);
  const resizableWidth = resizableColumns.reduce((total, column) => total + column.size, 0);

  if (containerWidth <= 0 || resizableWidth === 0) return {};

  const scale = Math.max(1, (containerWidth - fixedWidth) / resizableWidth);

  return Object.fromEntries(resizableColumns.map((column) => [column.id, Math.floor(column.size * scale)]));
}

export function useColumnSizing(
  storageKey: string | undefined
): [ColumnSizingState, (columnSizing: ColumnSizingState) => void] {
  const key = storageKey ? `data-table-column-sizing:${storageKey}` : undefined;
  const raw = useSyncExternalStore(
    subscribe,
    () => (key ? getSessionItem(key) : null),
    () => null
  );
  const columnSizing = parseColumnSizing(raw);

  const setColumnSizing = (next: ColumnSizingState) => {
    if (!key) return;

    setSessionItem(key, JSON.stringify(next));
    listeners.forEach((listener) => listener());
  };

  return [columnSizing, setColumnSizing];
}
